import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/ocorenApi";

const formatDay = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#09090B] text-white text-xs px-3 py-2 rounded-md border border-[#27272A]">
      <div className="font-semibold mb-1 font-mono-data">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span
            className="w-2 h-2 inline-block"
            style={{ background: p.color }}
          />
          <span className="uppercase tracking-[0.1em] text-[10px] text-zinc-400">
            {p.name}
          </span>
          <span className="font-mono-data ml-auto">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export const TimelineChart = ({ days = 30 }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get(`/ocoren/stats/timeline?days=${days}`)
      .then((res) => {
        if (!alive) return;
        const series = (res.data?.series || []).map((d) => ({
          ...d,
          dia_label: formatDay(d.dia),
        }));
        setData(series);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days]);

  return (
    <section
      data-testid="timeline-chart-section"
      className="bg-white border border-[#E4E4E7] rounded-md p-6"
    >
      <div className="mb-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#52525B] font-bold">
            Série Temporal
          </div>
          <h2 className="font-heading text-xl font-semibold text-[#09090B] mt-1">
            Entregas por dia
          </h2>
        </div>
        <TrendingUp size={20} className="text-[#52525B]" strokeWidth={1.5} />
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#52525B]">
          Carregando…
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#52525B]">
          Sem dados temporais ainda. Importe arquivos OCOREN para visualizar.
        </div>
      ) : (
        <div className="h-64" data-testid="timeline-chart-canvas">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
            <AreaChart
              data={data}
              margin={{ top: 5, right: 8, left: 4, bottom: 0 }}
            >
              <defs>
                <linearGradient id="entregasFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="problemasFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="outrosFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#09090B" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#09090B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="2 4"
                stroke="#E4E4E7"
                vertical={false}
              />
              <XAxis
                dataKey="dia_label"
                stroke="#52525B"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: "#E4E4E7" }}
              />
              <YAxis
                stroke="#52525B"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                width={48}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
                iconType="square"
                iconSize={10}
              />
              <Area
                type="monotone"
                dataKey="outros"
                name="Outros"
                stroke="#09090B"
                strokeWidth={1.5}
                fill="url(#outrosFill)"
                stackId="1"
              />
              <Area
                type="monotone"
                dataKey="problemas"
                name="Problemas"
                stroke="#F43F5E"
                strokeWidth={1.5}
                fill="url(#problemasFill)"
                stackId="1"
              />
              <Area
                type="monotone"
                dataKey="entregas"
                name="Entregas"
                stroke="#10B981"
                strokeWidth={1.75}
                fill="url(#entregasFill)"
                stackId="1"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
};

export default TimelineChart;
